import { when } from '@execonline-inc/maybe-adapter';
import { useEffect, useState } from 'react';
import { warnAndNotify } from '../../../../Honeybadger';
import SegmentStore from '../../../../SegmentStore';
import { autoSavingFormFields } from '../../../SegmentReactions/Functions';
import { FieldDescription, FieldValue } from '../../Types';
import { convertToFieldValue, fieldDescriptionPrefillSeed } from '../Helpers';
import { fieldHasValue, fieldValueM, validateField, whenMaxLengthValid } from './Functions';

function useFormField(
  segmentStore: SegmentStore,
  fieldDescription: FieldDescription,
  uuid: string,
) {
  const [fieldValue, setFieldValue] = useState<FieldValue[]>([]);

  useEffect(() => {
    const saved = fieldValueM(segmentStore.results, uuid);
    // DVLP-7754 / DVLP-7781 precedence: participant's own saved answer
    // wins; otherwise seed from the server-supplied prefill in
    // +fieldDescription.value+ (backend hydrates this from
    // +AttributableAttribute+). String field: use the value as-is.
    // Multi-select: resolve each value against the field's options to
    // rebuild +FieldValue+ entries the selection components consume.
    // Values that don't match an option are dropped (the backend has
    // already Honeybadger-notified on the miss).
    const seeded = saved.length === 0 ? fieldDescriptionPrefillSeed(fieldDescription) : saved;

    updateFieldValue(seeded);
  }, []);

  const updateFieldValue = (value: FieldValue[]) => {
    if (!Array.isArray(value)) {
      warnAndNotify(
        'badPayload',
        `The wrong data is coming from segmentStore results computed`,
        value,
      );
    }
    segmentStore.addEmbeddedFormFieldOutput({
      id: uuid,
      value: value,
      isValid: validateField(value, fieldDescription),
      isRequired: fieldDescription.mode === 'required',
    });

    setFieldValue(value);
  };

  const saveFieldValue = (value: FieldValue[]) => {
    updateFieldValue(value);
    autoSavingFormFields(segmentStore);
  };

  const addValue = (value: FieldValue) => {
    const newFieldValue = fieldValue
      .filter((v) => v.label !== value.label)
      .concat(convertToFieldValue(value.label, value.value));

    saveFieldValue(newFieldValue);
  };

  const removeValue = (value: FieldValue) => {
    const newFieldValue = fieldValue.filter((v) => v.label !== value.label);

    saveFieldValue(newFieldValue);
  };

  const validateAndAddValue = (option: FieldValue) =>
    whenMaxLengthValid(fieldValue, fieldDescription).do(() => addValue(option));

  const onValueChange = (option: FieldValue) =>
    when(fieldHasValue(option, fieldValue), true)
      .do(() => removeValue(option))
      .elseDo(() => validateAndAddValue(option));

  return {
    fieldValue: fieldValue,
    isRequired: fieldDescription.mode === 'required',
    addValue,
    removeValue,
    onValueChange,
    saveFieldValue,
  };
}

export default useFormField;
